import {getStoppageDuration} from '@domain/stoppages'
import {DateTimePeriod} from '@hconnect/common/components'
import {useSorting} from '@hconnect/common/hooks/useSorting'
import {StoppageEvent, StoppageItemKey} from '@hconnect/common/stoppages/types/stoppage.types'
import {StoppageDto, TimeZone} from '@hconnect/common/types'
import {Column, customThemeConstants, DataTable, PaginationOptions} from '@hconnect/uikit/src/lib2'
import {SortDirection, SxProps, Theme} from '@mui/material'
import {TFunction} from 'i18next'
import {get, isEqual, orderBy} from 'lodash'
import {FC, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'

const generateColumns = (translationFn: TFunction, timezone: TimeZone): Column<StoppageDto>[] => [
  {
    key: 'title',
    label: translationFn('stoppageAnalysis.label.stoppageTitle'),
    sortable: true,
    customTemplate: (stoppage) => stoppage.title
  },
  {
    key: 'stoppageCode',
    label: translationFn('stoppageAnalysis.label.stoppageCode'),
    sortable: true,
    customTemplate: (stoppage) => ('stoppageCode' in stoppage ? stoppage.stoppageCode : '-')
  },
  {
    key: 'stoppageStart',
    label: translationFn('stoppageAnalysis.label.stoppageStart'),
    sortable: true,
    customTemplate: (stoppage) => (
      <DateTimePeriod start={stoppage.stoppageStart} timezone={timezone} />
    )
  },
  {
    key: 'stoppageEnd',
    label: translationFn('stoppageAnalysis.label.stoppageEnd'),
    sortable: true,
    customTemplate: (stoppage) =>
      stoppage.stoppageEnd ? (
        <DateTimePeriod start={stoppage.stoppageEnd} timezone={timezone} />
      ) : (
        '-'
      )
  },
  {
    key: 'duration',
    label: translationFn('stoppageAnalysis.label.stoppageDuration'),
    sortable: true,
    customTemplate: ({stoppageStart, stoppageEnd}) =>
      getStoppageDuration(stoppageStart, stoppageEnd)
  }
]

const sortStoppages = (
  stoppages: StoppageEvent[],
  orderByKey: StoppageItemKey,
  sortDir: SortDirection
): StoppageEvent[] =>
  orderBy(
    stoppages,
    [
      (stoppages) => {
        if (orderByKey === 'hasSalesImpact') {
          return stoppages.hasSalesImpact
        }
        if (orderByKey === 'timeFrame') {
          return stoppages.stoppageStart
        }
        return get(stoppages, orderByKey, '')?.toString().toLowerCase()
      }
    ],
    [sortDir]
  )

type StoppageAnalysisListProps = {
  data: StoppageEvent[]
  timezone: TimeZone
  paginationOptions: PaginationOptions
  onRowClick?: (stoppage: StoppageEvent) => void
  stoppageToPreview?: StoppageDto
}

const rowSx = (selected: boolean): SxProps<Theme> => [
  selected &&
    ((theme) => ({
      backgroundColor: theme.palette.grey[200]
    })),
  {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: customThemeConstants().palette.primaryBackgroundColor
    },
    td: {
      fontSize: 16,
      lineHeight: '40px'
    }
  }
]

export const StoppageAnalysisList: FC<StoppageAnalysisListProps> = ({
  timezone,
  data,
  onRowClick,
  stoppageToPreview,
  paginationOptions
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const columns = useMemo(() => generateColumns(t, timezone), [t, timezone])

  const {
    sortedList = [],
    onSortClick,
    orderByKey,
    sortDir
  } = useSorting<StoppageEvent, StoppageItemKey>({
    initialSortDir: false,
    initialOrderByKey: 'timeFrame',
    data,
    sort: sortStoppages
  })

  const sortDirection = !sortDir ? undefined : sortDir

  return (
    <DataTable<StoppageEvent>
      columns={columns}
      data={sortedList}
      onSort={(_, key: StoppageItemKey) => onSortClick(key)}
      sortedBy={orderByKey}
      sortDirection={sortDirection}
      emptyMessage={t('stoppageAnalysis.message.noData')}
      paginationOptions={paginationOptions}
      onRowClick={(_, item) => {
        onRowClick?.(item)
      }}
      rowSx={(item) => rowSx(isEqual(item, stoppageToPreview))}
    />
  )
}
